import React, { useEffect, useRef, useState } from "react";
import { BrowserRouter } from "react-router-dom";
import { ConfigProvider, Modal } from "antd";
import Routing from "./routes/Routing";
import { APP_VERSION } from "./appVersion";

const RELOAD_FLAG = "app-hard-reload-version";
const DISMISSED_FLAG = "app-dismissed-version";

function getVersionManifestUrl() {
  return `/version.json?t=${Date.now()}`;
}

function App() {
  const dismissedVersion = useRef(null);
  const [availableVersion, setAvailableVersion] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const checkVersion = async () => {
      try {
        const response = await fetch(getVersionManifestUrl(), {
          cache: "no-store",
          headers: {
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
          },
        });
        const payload = await response.json();
        const version = String(payload?.version || "").trim();
        const runningVersion = String(APP_VERSION || "").trim();

        if (!version || !isMounted) return;

        if (version === runningVersion) {
          sessionStorage.removeItem(RELOAD_FLAG);
          sessionStorage.removeItem(DISMISSED_FLAG);
          setAvailableVersion(null);
          return;
        }

        const justReloadedFor = sessionStorage.getItem(RELOAD_FLAG);
        if (justReloadedFor) {
          sessionStorage.removeItem(RELOAD_FLAG);
          sessionStorage.setItem(DISMISSED_FLAG, version);
          setAvailableVersion(null);
          return;
        }

        const dismissed =
          dismissedVersion.current || sessionStorage.getItem(DISMISSED_FLAG);

        if (version !== dismissed) {
          setAvailableVersion(version);
        }
      } catch (error) {
        // Version checks must not affect normal use when the manifest is unavailable.
        console.warn("Unable to check the deployed app version", error);
      }
    };

    checkVersion();
    const intervalId = window.setInterval(checkVersion, 60000);
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") checkVersion();
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      isMounted = false;
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const hardReload = async () => {
    const nextVersion = availableVersion;
    setAvailableVersion(null);
    if (nextVersion) {
      sessionStorage.setItem(RELOAD_FLAG, nextVersion);
    }

    try {
      if ("serviceWorker" in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(
          registrations.map((registration) => registration.unregister())
        );
      }
      if ("caches" in window) {
        const cacheNames = await window.caches.keys();
        await Promise.all(
          cacheNames.map((cacheName) => window.caches.delete(cacheName))
        );
      }
    } catch (error) {
      console.warn("Unable to clear browser caches before reloading", error);
    } finally {
      const url = new URL(window.location.href);
      url.searchParams.delete("_reload");
      url.searchParams.set("_reload", String(Date.now()));
      window.location.replace(url.toString());
    }
  };

  return (
    <ConfigProvider
      theme={{
        token: {
          fontFamily: "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        },
      }}
    >
      <BrowserRouter basename="/">
        <Routing />
      </BrowserRouter>
      <Modal
        open={Boolean(availableVersion)}
        title="A new version is available"
        closable={false}
        maskClosable={false}
        cancelText="Later"
        okText="Hard Reload"
        onCancel={() => {
          dismissedVersion.current = availableVersion;
          if (availableVersion) {
            sessionStorage.setItem(DISMISSED_FLAG, availableVersion);
          }
          setAvailableVersion(null);
        }}
        onOk={hardReload}
      >
        Please reload to use the latest deployed version ({availableVersion}).
      </Modal>
    </ConfigProvider>
  );
}

export default App;
